import React, { useState, useCallback } from 'react';
import { Outlet } from 'react-router-dom';
import { 
  Box, 
  AppBar, 
  Toolbar, 
  Typography, 
  ListItemIcon, 
  ListItemText,
  FormControl,
  Select,
  MenuItem,
  Divider,
  ListSubheader,
  IconButton,
  Menu,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { 
  Dashboard as DashboardIcon, 
  Assessment, 
  Edit, 
  List as ListIcon, 
  People,
  Code,
  Settings,
  Menu as MenuIcon,
  CalendarToday,
  Brightness4 as Brightness4Icon,
  Brightness7 as Brightness7Icon,
  Logout as LogoutIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useSecteur } from '../contexts/SecteurContext';
import { useTheme as useCustomTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { appVersion } from '../utils/version';

// Groupes de menus
const mainMenuItems = [
  { text: 'Tableau de bord', icon: <DashboardIcon />, path: '/dashboard', permission: 'can_access_dashboard' },
  { text: 'Activités Mensuelles', icon: <Assessment />, path: '/monthly-activities', permission: 'can_access_monthly_activities' },
];

const pointageMenuItems = [
  { text: 'Saisie des pointages', icon: <Edit />, path: '/time-entry', permission: 'can_access_time_entry' },
  { text: 'Pointage Mensuel', icon: <CalendarToday />, path: '/', permission: 'can_access_pointage_mensuel' },
  { text: 'Calendrier Secteur', icon: <CalendarToday />, path: '/sector-calendar', permission: 'can_access_sector_calendar' },
];

const adminMenuItems = [
  { text: 'Moyens', icon: <People />, path: '/mandate-management', permission: 'can_access_mandate_management' },
  { text: 'Codes', icon: <Code />, path: '/releve-code-management', permission: 'can_access_releve_code_management' },
  { text: 'Secteurs', icon: <Settings />, path: '/sector-management', permission: 'can_access_sector_management' },
  { text: 'Activités', icon: <ListIcon />, path: '/admin/activities', permission: 'can_access_activities_admin' },
  { text: 'Utilisateurs', icon: <People />, path: '/admin/users', permission: 'can_access_user_admin' },
  { text: 'Rôles', icon: <ListIcon />, path: '/admin/roles', permission: 'can_access_roles_admin' },
];

function Layout() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { secteurs, selectedSecteur, setSelectedSecteur } = useSecteur();
  const [anchorEl, setAnchorEl] = useState(null);
  const { darkMode, toggleDarkMode } = useCustomTheme();
  const { user, roles, signOut } = useAuth();

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleMenuItemClick = useCallback((path) => {
    navigate(path);
    handleMenuClose();
  }, [navigate]);

  const handleSecteurChange = useCallback((event) => {
    const newSecteurId = event.target.value;
     setSelectedSecteur(newSecteurId);
  }, [setSelectedSecteur]);

  const handleLogout = async () => {
    await signOut();
    handleMenuClose();
  };

  const renderMenuItems = useCallback((items, title) => (
    [
      <ListSubheader key={title} sx={{ backgroundColor: 'background.paper' }}>{title}</ListSubheader>,
      ...items.filter(item => user && roles && roles[item.permission]).map((item) => (
        <MenuItem 
          key={item.text}
          onClick={() => handleMenuItemClick(item.path)}
          sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
        >
          <ListItemIcon sx={{ minWidth: 'auto' }}>{item.icon}</ListItemIcon>
          <ListItemText primary={item.text} />
        </MenuItem>
      )),
      <Divider key={`${title}-divider`} />
    ]
  ), [user, roles, handleMenuItemClick]);

 

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <AppBar position="fixed">
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton
              color="inherit"
              onClick={handleMenuOpen}
              edge="start"
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
              <Typography variant="h6" noWrap component="div">
                CFE-CGC Pointage
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.8 }}>
                v{appVersion}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <FormControl 
              variant="outlined" 
              size="small" 
              sx={{ 
                minWidth: isMobile ? 120 : 200,
                '& .MuiOutlinedInput-root': {
                  backgroundColor: (theme) => theme.palette.mode === 'dark' 
                  ? theme.palette.background.paper 
                  : theme.palette.grey[200],
                  color: (theme) => theme.palette.text.primary,
                  '& fieldset': {
                    borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.23)' : 'rgba(0, 0, 0, 0.23)',
                  },
                  '&:hover fieldset': {
                    borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.4)' : 'rgba(0, 0, 0, 0.4)',
                  },
                },
              }}
            >
              <Select
                value={selectedSecteur || ''}
                onChange={handleSecteurChange}
                displayEmpty
                inputProps={{ 'aria-label': 'Sélectionner un secteur' }}
              >
                {secteurs.map((secteur) => (
                  <MenuItem key={secteur.id} value={secteur.id}>
                    {secteur.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Informations utilisateur */}
            {user && (
              <Box 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  gap: 1,
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  padding: '4px 12px',
                  borderRadius: '4px'
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {user.full_name}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    backgroundColor: 'primary.dark',
                    padding: '2px 8px',
                    borderRadius: '4px',
                    fontWeight: 500
                  }}
                >
                  {user.role}
                </Typography>
              </Box>
            )}

            <IconButton
              color="inherit"
              onClick={toggleDarkMode}
              sx={{ ml: 1 }}
            >
              {darkMode ? <Brightness7Icon /> : <Brightness4Icon />}
            </IconButton>
          </Box>
        </Toolbar>

      </AppBar>

      {/* Menu déroulant */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            mt: '45px',
            maxHeight: '80vh',
            width: isMobile ? '90vw' : '300px'
          }
        }}
      >
        {renderMenuItems(mainMenuItems, 'Principal')}
        {renderMenuItems(pointageMenuItems, 'Pointages')}
        {renderMenuItems(adminMenuItems, 'Administration')}
        <Divider />
        <MenuItem 
          onClick={handleLogout}
          sx={{ 
            display: 'flex', 
            alignItems: 'center',
            gap: 1,
            color: 'error.main'
          }}
        >
          <ListItemIcon sx={{ minWidth: 'auto', color: 'error.main' }}>
            <LogoutIcon />
          </ListItemIcon>
          <ListItemText primary="Déconnexion" />
        </MenuItem>
      </Menu>

      {/* Contenu principal */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          mt: 8,
          width: '100%'
        }}
      >
        <Outlet />
      </Box>
    </Box>
  );
}

export default React.memo(Layout);
